import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UserAvatar } from "../components/UserAvatar";
import { toast } from "sonner";
import { Camera, Loader2, Save } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) setName(user.name || "");
  }, [user]);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      await api.patch("/profile", { name });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="settings-page">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Account</span>
      <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">Settings</h1>

      <div className="mt-8 border border-zinc-900 bg-white shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-6 sm:p-8 space-y-8">
        <div className="flex items-center gap-5">
          <div className="relative group">
            <UserAvatar name={user.name} email={user.email} src={user.avatar_url} className="h-20 w-20" fallbackClass="bg-primary/10 text-primary font-display font-black text-xl" testid="settings-avatar" />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="settings-upload-avatar"
              className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              title="Change photo"
            >
              {uploading ? <Loader2 className="h-5 w-5 text-white animate-spin" /> : <Camera className="h-5 w-5 text-white" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files?.[0])} />
          </div>
          <div>
            <p className="font-display font-bold text-lg">{user.name || user.email}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{user.role}</p>
            <button onClick={() => fileRef.current?.click()} className="mt-1 font-mono text-xs text-primary hover:underline">Change photo</button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
            <Input data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Email</Label>
            <Input value={user.email} disabled data-testid="settings-email-input" className="mt-1.5 rounded-none border-zinc-200 bg-zinc-50 text-zinc-500" />
          </div>
        </div>

        <Button data-testid="settings-save-btn" disabled={saving} onClick={saveName} className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save changes
        </Button>
      </div>
    </div>
  );
}
